/**
 * Performance History ECharts Adapter
 *
 * Transforms playground timeseries data into ECharts line/area chart options.
 * Supports both "index" (rebased) and "performance" (percentage) display modes.
 */

import type { EChartsCoreOption } from "echarts";
import * as echarts from "echarts";
import type {
  PerformanceHistoryAdapterProps,
  EChartAdapterResult,
  DataPoint,
  ExtendedAssetInfo,
} from "./types";
import { createCategoryAxisLabelInterval } from "./axisDateLabels";
import {
  buildProxyAwareLineSeries,
  findProxyExtensionForAsset,
  getProxyExtensionsFromStats,
} from "./proxySeriesUtils";

/**
 * Get ordinal suffix for a day number (1st, 2nd, 3rd, etc.)
 */
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

/**
 * Format date for axis labels (short format: "Jan 2024")
 */
function formatDateForAxis(date: Date): string {
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${month} ${year}`;
}

/**
 * Format date for tooltips (full format: "15th Jan 2026")
 */
function formatDateForTooltip(date: Date): string {
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}

/**
 * Format value based on display mode
 */
function formatValue(value: number, displayMode: string): string {
  // Format number intelligently: remove .0 for whole numbers, keep decimals otherwise
  // Use epsilon check to handle floating point precision issues
  const rounded = Math.round(value * 10) / 10;
  const formatted = Math.abs(rounded - Math.round(rounded)) < 0.001 
    ? Math.round(rounded).toString() 
    : rounded.toFixed(1);
  
  if (displayMode === "index") {
    return formatted;
  }
  return `${formatted}%`;
}

/**
 * Convert hex color to rgba string
 */
function hexToRgba(hex: string, alpha: number): string {
  // Remove # if present
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Create area style gradient for a color using ECharts graphic.LinearGradient
 */
function createAreaGradient(
  color: string,
  opacity: number = 0.3
): echarts.graphic.LinearGradient {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: hexToRgba(color, opacity) },
    { offset: 1, color: hexToRgba(color, 0) },
  ]);
}

/**
 * Create a diagonal line pattern for chart background
 * Returns a pattern object that can be used as backgroundColor in ECharts
 */
function createPatternBackground(isDarkMode: boolean): object {
  // Create a canvas pattern for diagonal lines
  const patternSize = 10;
  const canvas = document.createElement("canvas");
  canvas.width = patternSize;
  canvas.height = patternSize;
  const ctx = canvas.getContext("2d");
  
  if (ctx) {
    // Set transparent background
    ctx.clearRect(0, 0, patternSize, patternSize);
    
    // Draw diagonal line
    ctx.strokeStyle = isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, patternSize);
    ctx.lineTo(patternSize, 0);
    ctx.stroke();
  }
  
  return {
    image: canvas,
    repeat: "repeat",
  };
}

/**
 * Calculate a "nice" number (round number) for axis intervals
 * Based on the "nice numbers" algorithm for pretty axis labels
 */
function calculateNiceNumber(range: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

/**
 * Calculate smart Y-axis domain with clean intervals
 */
function calculateYDomain(seriesData: any[], desiredTicks: number = 5): { 
  min: number; 
  max: number; 
  interval: number;
} {
  const allValues: number[] = [];
  seriesData.forEach((s) => {
    if (Array.isArray(s.data)) {
      s.data.forEach((v: any) => {
        if (typeof v === "number" && !isNaN(v)) {
          allValues.push(v);
        }
      });
    }
  });

  if (allValues.length === 0) return { min: 0, max: 100, interval: 20 };

  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const dataRange = dataMax - dataMin;

  // Add padding to the data range
  const paddingPercent = 0.1;
  const paddedRange = dataRange * (1 + paddingPercent * 2);
  
  // Calculate a nice interval
  const rawInterval = paddedRange / desiredTicks;
  const interval = calculateNiceNumber(rawInterval, true);

  // Round min down and max up to nearest interval
  const niceMin = Math.floor(dataMin / interval) * interval;
  const niceMax = Math.ceil(dataMax / interval) * interval;

  return { min: niceMin, max: niceMax, interval };
}

/**
 * Build ECharts option for performance history charts
 */
export function buildPerformanceHistoryOption(
  props: PerformanceHistoryAdapterProps
): EChartAdapterResult {
  const {
    rawData,
    selectedAssets,
    assetDisplayNames,
    assetColors,
    colorPalette,
    chartSettings,
    visualSettings,
    isDarkMode,
    assetStats,
    assetMetadata,
  } = props;

  const displayMode = chartSettings.displayMode || "performance";
  const showAreaGradient = visualSettings.showAreaGradient ?? true;
  const smoothLines = visualSettings.smoothLines ?? false;
  const showGridRows = visualSettings.showGridRows ?? true;
  const showGridColumns = visualSettings.showGridColumns ?? false;
  const showCrosshair = visualSettings.showCrosshair ?? true;
  const showTooltips = visualSettings.showTooltips ?? true;
  const showXAxis = visualSettings.showXAxis ?? true;
  const showYAxis = visualSettings.showYAxis ?? true;
  const showPatternBackground = visualSettings.showPatternBackground ?? false;

  // Handle empty data
  if (!rawData || rawData.length === 0 || selectedAssets.length === 0) {
    return {
      option: {},
      chartType: "line",
      hasData: false,
    };
  }

  // Extract raw dates for tooltip and formatted dates for x-axis
  const rawDates = rawData.map((point: DataPoint) => point.date);
  const axisLabelInterval = createCategoryAxisLabelInterval(rawDates);
  const dates = rawData.map((point: DataPoint) => {
    const date = point.date;
    return date instanceof Date ? formatDateForAxis(date) : String(date);
  });

  const proxyExtensions = getProxyExtensionsFromStats(assetStats);
  const metadataByAsset = assetMetadata as Record<string, ExtendedAssetInfo>;

  // Build series for each asset (with optional dashed proxy edge segments)
  const series = selectedAssets.flatMap((assetId: string, index: number) => {
    const color = assetColors[assetId] || colorPalette[index % colorPalette.length];
    const displayName = assetDisplayNames[assetId] || assetId;
    const extension = findProxyExtensionForAsset(
      assetId,
      metadataByAsset,
      proxyExtensions
    );

    return buildProxyAwareLineSeries({
      assetId,
      displayName,
      color,
      rawData,
      extension,
      smoothLines,
      showAreaGradient,
      createAreaGradient,
    });
  });

  // Theme colors
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const gridColor = isDarkMode ? "#475569" : "#e2e8f0";
  const axisLineColor = isDarkMode ? "#64748b" : "#94a3b8";

  // Determine Y-axis title based on display mode
  const yAxisTitle = displayMode === "index" ? "Index Value" : "Performance (%)";

  // Calculate smart Y-axis domain with clean intervals
  const yDomain = calculateYDomain(series, 5);

  // Build the ECharts option
  const option: EChartsCoreOption = {
    backgroundColor: showPatternBackground ? createPatternBackground(isDarkMode) : "transparent",
    tooltip: showTooltips
      ? {
          trigger: "axis",
          axisPointer: {
            type: showCrosshair ? "cross" : "line",
            crossStyle: {
              color: axisLineColor,
            },
          },
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: {
            color: textColor,
          },
          formatter: (params: any) => {
            if (!Array.isArray(params) || params.length === 0) return "";

            // Get the raw date using dataIndex and format it for tooltip
            const dataIndex = params[0].dataIndex;
            const rawDate = rawDates[dataIndex];
            const dateLabel = rawDate instanceof Date 
              ? formatDateForTooltip(rawDate) 
              : params[0].axisValue;
            let content = `<div style="font-weight: 600; margin-bottom: 8px;">${dateLabel}</div>`;

            params.forEach((param: any) => {
              if (param.value !== null && param.value !== undefined) {
                const formattedValue = formatValue(param.value, displayMode);
                content += `
                  <div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
                    <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${param.color};"></span>
                    <span style="flex: 1;">${param.seriesName}:</span>
                    <span style="font-weight: 600;">${formattedValue}</span>
                  </div>
                `;
              }
            });

            return content;
          },
        }
      : undefined,
    legend: {
      show: false, // Use custom legend from EChartRenderer
    },
    grid: {
      left: showYAxis ? 80 : 10,
      right: 20,
      top: 20,
      bottom: showXAxis ? 60 : 30,
      containLabel: false,
      borderWidth: 0,
    },
    xAxis: {
      type: "category",
      data: dates,
      boundaryGap: false,
      name: showXAxis ? "Date" : "",
      nameLocation: "center",
      nameGap: 40,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      show: true, // Always show for grid lines, but control individual elements
      axisLine: {
        show: false,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showXAxis, // Toggle labels based on showXAxis setting
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        interval: axisLabelInterval,
        margin: 10,
      },
      splitLine: {
        show: showGridColumns,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    yAxis: {
      type: "value",
      name: showYAxis ? yAxisTitle : "",
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      min: yDomain.min,
      max: yDomain.max,
      interval: yDomain.interval,
      show: true, // Always show for grid lines, but control individual elements
      axisLine: {
        show: showYAxis,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showYAxis, // Toggle labels based on showYAxis setting
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => formatValue(value, displayMode),
        margin: 15,
      },
      splitLine: {
        show: showGridRows,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    series,
    color: colorPalette,
  };

  return {
    option,
    chartType: showAreaGradient ? "area" : "line",
    hasData: true,
  };
}
